import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils/cn";
import { useTour } from "@reactour/tour";
import { useEffect } from "react";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

// Create a custom context to track document visibility
const SheetVisibilityContext = React.createContext({
  documentVisible: true,
});

// Custom portal that tracks document visibility
const SheetPortal = ({ ...props }: SheetPrimitive.DialogPortalProps) => {
  const [documentVisible, setDocumentVisible] = React.useState(true);

  // Track document visibility changes
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setDocumentVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <SheetVisibilityContext.Provider value={{ documentVisible }}>
      <SheetPrimitive.Portal {...props} />
    </SheetVisibilityContext.Provider>
  );
};
SheetPortal.displayName = SheetPrimitive.Portal.displayName;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
    className?: string;
  }
>(({ className, ...props }, ref) => {
  useEffect(() => {
    const applyBackdropBlurClasses = () => {
      const overlays = Array.from(
        document.querySelectorAll<HTMLElement>(".unique-slideover"),
      );
      const contents = Array.from(
        document.querySelectorAll<HTMLElement>(".unique-slideover-content"),
      );

      overlays.forEach((overlay, index) => {
        const contentBeneath = contents[index - 1];
        const shouldBlur =
          index === 0 || contentBeneath.dataset.sheetSize === "full";

        overlay.classList.toggle("slideover--blurred", shouldBlur);
      });
    };

    // Run once immediately
    applyBackdropBlurClasses();

    const observer = new MutationObserver(() => {
      applyBackdropBlurClasses();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <SheetPrimitive.Overlay
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[102]",
        "unique-slideover",
        className,
      )}
      {...props}
      ref={ref}
    />
  );
});
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

export const sheetVariants = cva(
  "fixed z-[103] gap-4 bg-card p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
      size: {
        default: "",
        large: "md:min-w-[750px] lg:min-w-[1000px]",
        full: "w-screen min-w-screen max-w-none sm:max-w-none",
      },
    },
    defaultVariants: {
      side: "right",
      size: "default",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  disableOutsideClose?: boolean;
  hideCloseButton?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      size,
      className,
      children,
      disableOutsideClose,
      hideCloseButton = false,
      ...props
    },
    ref,
  ) => {
    const { documentVisible } = React.useContext(SheetVisibilityContext);
    const { isOpen: isTourOpen } = useTour();
    const shouldPreventClose =
      disableOutsideClose || !documentVisible || isTourOpen;

    // Prevent closing the sheet in conditions where outside clicks should be ignored
    const handlePointerDownOutside = (event: Event) => {
      if (shouldPreventClose) {
        event.preventDefault();
      }
    };
    // Keep focus interactions consistent with the click handling above
    const handleFocusOutside = (event: Event) => {
      if (shouldPreventClose) {
        event.preventDefault();
      }
    };

    // Check if there are any modal popovers open and prevent escape from closing the sheet
    const handleEscapeKeyDown = (event: Event) => {
      const modalPopovers = document.querySelectorAll(
        '[data-modal-popover="true"]',
      );
      if (modalPopovers.length > 0) {
        event.preventDefault();
      }
    };

    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={ref}
          className={cn(
            sheetVariants({ side, size }),
            size === "full"
              ? "overflow-y-auto"
              : "min-w-full overflow-y-auto sm:min-w-[600px]",
            "unique-slideover-content",
            className,
          )}
          data-sheet-size={size ?? "default"}
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-2 text-left", className)}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  addSlideoverStyles,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  addSlideoverStyles?: boolean;
  className?: string;
}) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      addSlideoverStyles &&
        "bg-card border-t-light-primary w-full gap-y-2 border-t p-6",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-foreground text-lg font-semibold", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
